import React, { useEffect, useRef, useState } from 'react';
import bedrockService from '../services/bedrockService';

export const BULK_POLL_MS = 5000;
// Reload the report every few polls so tables fill in as files finish.
export const RELOADS_EVERY_POLLS = 6;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const summarize = ({ total, completed, failed, outcomes = {} }) => {
  const parts = [];
  if (outcomes.extracted) parts.push(`${outcomes.extracted} extracted`);
  if (outcomes.not_applicable) parts.push(`${outcomes.not_applicable} not applicable`);
  if (outcomes.parse_error) parts.push(`${outcomes.parse_error} unreadable replies`);
  if (outcomes.none) parts.push(`${outcomes.none} with no prompt`);
  if (failed) parts.push(`${failed} failed`);
  return `${completed + failed} of ${total} re-run${parts.length ? `: ${parts.join(', ')}` : ''}.`;
};

/**
 * BulkRerunBanner
 *
 * "Bring client up to date": counts the client's documents whose eMoney
 * data is missing or came from an older prompt (rows with rerun ===
 * 'needed') and re-runs extraction on all of them server-side, a few at a
 * time. Progress is polled; the report reloads as tables fill in. Mount it
 * with key={clientId} so switching clients starts fresh -- the server
 * keeps going either way.
 */
const BulkRerunBanner = ({ clientId, docs, onReload }) => {
  const [batch, setBatch] = useState(null); // { total, completed, failed, outcomes, status }
  const [starting, setStarting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; }, []);

  const needed = (docs || []).filter((d) => d.rerun === 'needed').length;
  const expired = (docs || []).filter((d) => d.rerun === 'expired').length;
  const running = starting || (batch && batch.status === 'running');

  const start = async () => {
    setStarting(true);
    setError('');
    setMessage('');
    try {
      const started = await bedrockService.bulkRerunExtraction(clientId);
      if (!aliveRef.current) return;
      if (!started.batch_id) {
        setMessage('Everything is already up to date.');
        return;
      }
      setBatch({ status: 'running', total: started.total, completed: 0, failed: 0, outcomes: {} });
      setStarting(false);
      for (let polls = 1; ; polls += 1) {
        await sleep(BULK_POLL_MS);
        if (!aliveRef.current) return;
        const status = await bedrockService.getBulkRerun(started.batch_id);
        if (!aliveRef.current) return;
        setBatch(status);
        if (status.status !== 'running') {
          if (status.status === 'completed') setMessage(summarize(status));
          else setError(status.error || 'The bulk re-run stopped.');
          await onReload();
          return;
        }
        if (polls % RELOADS_EVERY_POLLS === 0) await onReload();
      }
    } catch (err) {
      console.error('Error running the bulk re-run:', err);
      if (aliveRef.current) {
        const detail = err && err.response && err.response.data && err.response.data.detail;
        setError(detail || 'Could not start the bulk re-run.');
      }
    } finally {
      if (aliveRef.current) setStarting(false);
    }
  };

  if (!running && !message && !error && needed === 0 && expired === 0) return null;

  const done = batch ? batch.completed + batch.failed : 0;
  const percent = batch && batch.total ? Math.round((done / batch.total) * 100) : 0;

  return (
    <div className="alert alert-info py-2 small d-print-none" data-testid="bulk-rerun-banner">
      {running && batch ? (
        <>
          <div className="d-flex justify-content-between mb-1">
            <span data-testid="bulk-rerun-progress">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Re-running extraction: {done} of {batch.total}{batch.failed ? ` · ${batch.failed} failed` : ''}
            </span>
            <span className="text-muted">You can leave this page; it keeps running.</span>
          </div>
          <div className="progress" style={{ height: 6 }}>
            <div className="progress-bar" role="progressbar" style={{ width: `${percent}%` }}
              aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}></div>
          </div>
        </>
      ) : (
        <div className="d-flex flex-wrap align-items-center gap-2">
          {needed > 0 && (
            <>
              <span data-testid="bulk-rerun-needed">
                <i className="bi bi-arrow-repeat me-1"></i>
                {needed} {needed === 1 ? "document doesn't" : "documents don't"} have current eMoney data.
              </span>
              <button type="button" className="btn btn-sm btn-primary" onClick={start} disabled={starting}
                title={`Runs each document's extraction prompt again: ${needed} model ${needed === 1 ? 'call' : 'calls'}.`}>
                {starting ? 'Starting…' : `Re-run extraction for all ${needed}`}
              </button>
            </>
          )}
          {message && <span className="text-success" data-testid="bulk-rerun-summary">{message}</span>}
          {error && <span className="text-danger" data-testid="bulk-rerun-error">{error}</span>}
        </div>
      )}
      {expired > 0 && !running && (
        <div className="text-muted mt-1" data-testid="bulk-rerun-expired">
          {expired} {expired === 1 ? "document can't" : "documents can't"} be re-run because the stored file has expired; re-upload {expired === 1 ? 'it' : 'them'} to refresh.
        </div>
      )}
    </div>
  );
};

export default BulkRerunBanner;
